import React, { useState, useEffect, useRef } from 'react';
import CONFIG from '../../config/config';

interface ContactUsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  subject: string;
  service: string;
  category: string;
  subcategory: string;
  description: string;
  privacy: boolean;
  files: File[];
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  subject?: string;
  subcategory?: string;
  description?: string;
  privacy?: string;
  files?: string;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];

export const ContactUsModal: React.FC<ContactUsModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    subject: '',
    service: 'زاد',
    category: 'استفسارات عامة عن منصه زاد',
    subcategory: '',
    description: '',
    privacy: false,
    files: []
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error' | 'warning'; message: string; ticketId?: string } | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Focus trap
      modalRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      // Reset form when closing
      setTimeout(() => {
        resetForm();
      }, 300);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      subject: '',
      service: 'زاد',
      category: 'استفسارات عامة عن منصه زاد',
      subcategory: '',
      description: '',
      privacy: false,
      files: []
    });
    setErrors({});
    setTouched(new Set());
    setSubmitStatus(null);
    setShowSuccessScreen(false);
    setShowErrorScreen(false);
  };

  const validateField = (name: string, value: any): string | undefined => {
    switch (name) {
      case 'firstName':
        if (!value || value.trim().length < 2) {
          return 'يجب أن يحتوي الاسم الأول على حرفين على الأقل';
        }
        break;
      case 'lastName':
        if (!value || value.trim().length < 2) {
          return 'يجب أن يحتوي اسم العائلة على حرفين على الأقل';
        }
        break;
      case 'phone':
        if (!value || value.trim().length === 0) {
          return 'رقم الهاتف مطلوب';
        }
        const cleanPhone = value.replace(/\D/g, '');

        if (cleanPhone.length === 10 && cleanPhone.startsWith('05')) {
        } else if (cleanPhone.length === 9 && cleanPhone.startsWith('5')) {
        } else {
          return 'رقم الهاتف غير صالح. يجب أن يكون 10 أرقام تبدأ بـ 05 أو 9 أرقام تبدأ بـ 5';
        }
        break;
      case 'email':
        if (!value || value.trim().length === 0) {
          return 'البريد الإلكتروني مطلوب';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'يرجى إدخال بريد إلكتروني صحيح';
        }
        break;
      case 'subject':
        if (!value || value.trim().length < 5) {
          return 'الموضوع يجب أن يكون على الأقل 5 أحرف';
        }
        break;
      case 'subcategory':
        if (!value || value.trim().length === 0) {
          return 'التصنيف الفرعي مطلوب';
        }
        if (!['تحميل الملفات', 'تحديث البيانات', 'أخرى'].includes(value)) {
          return 'التصنيف الفرعي غير صالح';
        }
        break;
      case 'description':
        if (!value || value.trim().length < 10) {
          return 'الوصف يجب أن يكون على الأقل 10 أحرف';
        }
        break;
      case 'privacy':
        if (!value) {
          return 'يجب الموافقة على سياسة الخصوصية';
        }
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    Object.keys(formData).forEach((key) => {
      if (key !== 'files' && key !== 'service' && key !== 'category') {
        const error = validateField(key, formData[key as keyof FormData]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (touched.has(name)) {
      const error = validateField(name, type === 'checkbox' ? checked : value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setTouched(prev => new Set(prev).add(name));

    const error = validateField(name, type === 'checkbox' ? checked : value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (formData.files.length + files.length > MAX_FILES) {
      setErrors(prev => ({
        ...prev,
        files: `يمكنك تحميل ${MAX_FILES} ملفات كحد أقصى`
      }));
      return;
    }

    for (const file of files) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          files: 'يُسمح فقط بملفات الصور (JPG, PNG, WEBP, GIF) و PDF'
        }));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          files: `حجم الملف ${file.name} يتجاوز 10 ميجابايت`
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));

    setErrors(prev => ({
      ...prev,
      files: undefined
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allFields = new Set(Object.keys(formData).filter(key => key !== 'files' && key !== 'service' && key !== 'category'));
    setTouched(allFields);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('subcategory', formData.subcategory);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('privacy', formData.privacy.toString());

      formData.files.forEach((file) => {
        formDataToSend.append('files[]', file);
      });

      const response = await fetch(`${CONFIG.api}/api/contact-us/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const hasFileErrors = result.results?.files_failed > 0;

        if (hasFileErrors) {
          const fileErrors = result.results?.errors?.join('\n') || 'فشل تحميل بعض الملفات';
          setSubmitStatus({
            type: 'error',
            message: `تم إنشاء التذكرة بنجاح ولكن فشل تحميل الملفات:\n\n${fileErrors}\n\nرقم التذكرة: ${result.ticket_id}`,
            ticketId: result.ticket_id
          });
          setShowErrorScreen(true);
        } else {
          setSubmitStatus({
            type: 'success',
            message: result.message || 'شكراً لك على تواصلك معنا. سنرد عليك في أقرب وقت ممكن.',
            ticketId: result.ticket_id
          });
          setShowSuccessScreen(true);
        }
      } else {
        const errorMessage = result.message || 'حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى.';
        setSubmitStatus({
          type: 'error',
          message: errorMessage
        });

        if (result.errors && Object.keys(result.errors).length > 0) {
          setErrors(result.errors);
        } else {
          setShowErrorScreen(true);
        }
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'حدث خطأ في الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.'
      });
      setShowErrorScreen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.firstName.trim().length >= 2 &&
      formData.lastName.trim().length >= 2 &&
      formData.phone.trim().length > 0 &&
      formData.email.trim().length > 0 &&
      formData.subject.trim().length >= 5 &&
      formData.subcategory.trim().length > 0 &&
      formData.description.trim().length >= 10 &&
      formData.privacy &&
      Object.keys(errors).every(key => !errors[key as keyof FormErrors])
    );
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSuccessClose = () => {
    onClose();
    resetForm();
  };

  const handleErrorRetry = () => {
    setShowErrorScreen(false);
    setSubmitStatus(null);
  };

  if (!isOpen) return null;

  return (
    <div
      className="contact-us-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div
        className="contact-us-modal"
        ref={modalRef}
        tabIndex={-1}
      >
        <button
          className="contact-us-modal-close"
          onClick={onClose}
          aria-label="إغلاق النموذج"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {showSuccessScreen ? (
          <>
            <div className="contact-us-modal-header">
              <h2 id="contact-modal-title">هل تحتاج للتواصل معنا؟</h2>
              <p>املأ النموذج وسيقوم فريقنا بالرد في أسرع وقت ممكن</p>
            </div>

            <div className="contact-us-success-screen" style={{textAlign: 'center'}}>
              <div className="contact-us-success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="#17B26A"/>
                  <path d="M16 24L22 30L32 18" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="contact-us-success-title">تم الإرسال بنجاح!</h3>
              <p className="contact-us-success-inline">
                تم إرسال طلبك بنجاح! سنتواصل معك قريبًا.
                {submitStatus?.ticketId && (
                  <span>

                    <span className="contact-us-ticket-id-inline"> رقم التذكرة: {submitStatus.ticketId}</span>
                  </span>
                )}
              </p>
            </div>
          </>
        ) : showErrorScreen ? (
          <div className="contact-us-error-screen">
            <div className="contact-us-error-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#F04438"/>
                <path d="M18 18L30 30M30 18L18 30" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="contact-us-error-title">حدث خطأ</h3>
            <p className="contact-us-error-message">{submitStatus?.message || 'حدث خطأ أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى.'}</p>
            <button
              type="button"
              className="contact-us-error-button"
              onClick={handleErrorRetry}
            >
              إعادة المحاولة
            </button>
          </div>
        ) : (
          <>
            <div className="contact-us-modal-header">
              <h2 id="contact-modal-title">هل تحتاج للتواصل معنا؟</h2>
              <p>املأ النموذج وسيرد فريقنا في أسرع وقت ممكن</p>
            </div>

            {submitStatus && submitStatus.type === 'error' && !showErrorScreen && (
              <div className="contact-us-status contact-us-status-error">
                <div style={{ whiteSpace: 'pre-line' }}>{submitStatus.message}</div>
              </div>
            )}

        <form onSubmit={handleSubmit} className="contact-us-form" noValidate>
          <div className="contact-us-form-row">
            <div className="contact-us-form-group">
              <label htmlFor="firstName">الاسم الأول *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="أدخل الاسم الأول"
                aria-required="true"
                aria-invalid={!!errors.firstName}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              />
              {errors.firstName && touched.has('firstName') && (
                <span className="contact-us-error" id="firstName-error" role="alert">
                  {errors.firstName}
                </span>
              )}
            </div>

            <div className="contact-us-form-group">
              <label htmlFor="lastName">اسم العائلة *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="أدخل اسم العائلة"
                aria-required="true"
                aria-invalid={!!errors.lastName}
                aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              />
              {errors.lastName && touched.has('lastName') && (
                <span className="contact-us-error" id="lastName-error" role="alert">
                  {errors.lastName}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group">
              <label htmlFor="phone">رقم الهاتف *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="05xxxxxxxx"
                aria-required="true"
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {errors.phone && touched.has('phone') && (
                <span className="contact-us-error" id="phone-error" role="alert">
                  {errors.phone}
                </span>
              )}
            </div>

            <div className="contact-us-form-group">
              <label htmlFor="email">البريد الإلكتروني *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="example@email.com"
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && touched.has('email') && (
                <span className="contact-us-error" id="email-error" role="alert">
                  {errors.email}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group contact-us-form-group-full">
              <label htmlFor="subject">عنوان المشكلة *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="أدخل عنوان المشكلة"
                aria-required="true"
                aria-invalid={!!errors.subject}
                aria-describedby={errors.subject ? 'subject-error' : undefined}
              />
              {errors.subject && touched.has('subject') && (
                <span className="contact-us-error" id="subject-error" role="alert">
                  {errors.subject}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group">
              <label htmlFor="category">الفئة</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="استفسارات عامة عن منصه زاد">استفسارات عامة عن منصه زاد</option>
              </select>
            </div>

            <div className="contact-us-form-group">
              <label htmlFor="subcategory">الفئة الفرعية *</label>
              <select
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleInputChange}
                onBlur={handleBlur}
                aria-required="true"
                aria-invalid={!!errors.subcategory}
                aria-describedby={errors.subcategory ? 'subcategory-error' : undefined}
              >
                <option value="">اختر الفئة الفرعية</option>
                <option value="تحميل الملفات">تحميل الملفات</option>
                <option value="تحديث البيانات">تحديث البيانات</option>
                <option value="أخرى">أخرى</option>
              </select>
              {errors.subcategory && touched.has('subcategory') && (
                <span className="contact-us-error" id="subcategory-error" role="alert">
                  {errors.subcategory}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group contact-us-form-group-full">
              <label htmlFor="description">الوصف *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="أدخل وصفًا تفصيليًا للمشكلة"
                rows={5}
                aria-required="true"
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? 'description-error' : undefined}
              />
              {errors.description && touched.has('description') && (
                <span className="contact-us-error" id="description-error" role="alert">
                  {errors.description}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group contact-us-form-group-full">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
                className="contact-us-file-input"
                id="file-upload"
                aria-label="تحميل الملفات"
              />
              <button
                type="button"
                className="contact-us-file-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={formData.files.length >= MAX_FILES}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                إضافة ملفات (بحد أقصى {MAX_FILES} ملفات)
              </button>
              {errors.files && (
                <span className="contact-us-error" role="alert">
                  {errors.files}
                </span>
              )}

              {formData.files.length > 0 && (
                <div className="contact-us-files-list">
                  {formData.files.map((file, index) => (
                    <div key={index} className="contact-us-file-item">
                      <div className="contact-us-file-info">
                        <span className="contact-us-file-name">{file.name}</span>
                        <span className="contact-us-file-size">({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        type="button"
                        className="contact-us-file-remove"
                        onClick={() => removeFile(index)}
                        aria-label={`إزالة ${file.name}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="contact-us-form-row">
            <div className="contact-us-form-group contact-us-form-group-full">
              <label className="contact-us-checkbox-label">
                <input
                  type="checkbox"
                  name="privacy"
                  checked={formData.privacy}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  aria-required="true"
                  aria-invalid={!!errors.privacy}
                  aria-describedby={errors.privacy ? 'privacy-error' : undefined}
                />
                <span>
                  سيتم معالجة بياناتك الشخصية لمعالجة استفسارك. لمزيد من التفاصيل، يرجى زيارة{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    سياسة الخصوصية
                  </a>
                  . *
                </span>
              </label>
              {errors.privacy && touched.has('privacy') && (
                <span className="contact-us-error" id="privacy-error" role="alert">
                  {errors.privacy}
                </span>
              )}
            </div>
          </div>

          <div className="contact-us-form-row contact-us-form-actions">
            <button
              type="submit"
              className="contact-us-submit-button"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? 'جاري الإرسال...' : 'إرسال'}
            </button>
          </div>
        </form>
          </>
        )}
      </div>
    </div>
  );
};


